/**
 * shadcn's registry emits `<IconPlaceholder lucide="…" tabler="…" …/>` and relies on its
 * CLI to swap in the chosen icon library, but Radix Icons (our library, "legacy" in
 * shadcn) isn't supported by that transform. This shim maps the lucide name to the
 * Radix icon. `npm run shadcn:fix` points generated components here; a test fails
 * when a component uses a name this table doesn't map.
 */
import {
  CheckCircledIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  Cross2Icon,
  CrossCircledIcon,
  DotsHorizontalIcon,
  ExclamationTriangleIcon,
  InfoCircledIcon,
  MagnifyingGlassIcon,
  ReloadIcon,
  ViewVerticalIcon,
} from '@radix-ui/react-icons';
import type { IconProps } from '@radix-ui/react-icons/dist/types';
import type { ComponentType } from 'react';

export const LUCIDE_TO_RADIX: Record<string, ComponentType<IconProps>> = {
  CheckIcon,
  CircleCheckIcon: CheckCircledIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  InfoIcon: InfoCircledIcon,
  Loader2Icon: ReloadIcon,
  MoreHorizontalIcon: DotsHorizontalIcon,
  OctagonXIcon: CrossCircledIcon,
  PanelLeftIcon: ViewVerticalIcon,
  SearchIcon: MagnifyingGlassIcon,
  TriangleAlertIcon: ExclamationTriangleIcon,
  XIcon: Cross2Icon,
};

export interface IconPlaceholderProps extends IconProps {
  lucide: string;
  tabler?: string;
  hugeicons?: string;
  phosphor?: string;
  remixicon?: string;
}

export function IconPlaceholder({
  lucide,
  tabler: _tabler,
  hugeicons: _hugeicons,
  phosphor: _phosphor,
  remixicon: _remixicon,
  ...props
}: IconPlaceholderProps) {
  const Icon = LUCIDE_TO_RADIX[lucide];
  if (!Icon) {
    if (import.meta.env.DEV) console.warn(`IconPlaceholder: no Radix icon mapped for lucide "${lucide}"`);
    return null;
  }
  return <Icon aria-hidden {...props} />;
}
